// ---- Marketplace application health + logs --------------------------------

import { useQuery } from '@tanstack/react-query';
import { serviceApi, onPath, onKeys, unwrap } from '../_shared';

export interface AppHealth {
  healthy?: boolean;
  status?: string;
  message?: string;
  endpoint?: string;
  http_status?: number;
  response_time?: number;
}

export interface AppLogs {
  container?: string;
  logs?: string;
  error?: string | null;
}

/** Health of a marketplace-app instance (`marketplaces/:id/healthz`). Polls every
 *  30s while enabled (app instance, powered on). */
export function useApplicationHealth(
  serviceId: string | undefined,
  instanceId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: onKeys.instanceHealth(serviceId ?? '', instanceId ?? ''),
    enabled: enabled && !!serviceId && !!instanceId,
    retry: false,
    refetchInterval: enabled ? 30_000 : false,
    staleTime: 10_000,
    queryFn: async () => {
      const body = unwrap<{ instance?: { health?: AppHealth } }>(
        await serviceApi.get(onPath(serviceId!, `marketplaces/${instanceId}/healthz`)),
      );
      return body.instance?.health ?? {};
    },
  });
}

/** Container logs for a marketplace-app instance (`marketplaces/:id/logs`). */
export function useApplicationLogs(
  serviceId: string | undefined,
  instanceId: string | undefined,
  query?: { container?: string; lines?: number },
  enabled = true,
) {
  return useQuery({
    queryKey: ['on', serviceId ?? '', 'instances', instanceId ?? '', 'app-logs', JSON.stringify(query ?? {})],
    enabled: enabled && !!serviceId && !!instanceId,
    retry: false,
    refetchInterval: enabled ? 15_000 : false,
    queryFn: async () => {
      const body = unwrap<{ logs?: AppLogs }>(
        await serviceApi.get(onPath(serviceId!, `marketplaces/${instanceId}/logs`), { params: query }),
      );
      return body.logs ?? {};
    },
  });
}
